import { useState, useEffect, useMemo } from 'react';
import { useProgressionsStore } from '@/store/progressions-store';
import { useAppViewStore } from '@/store/app-view-store';
import { useAuthStore } from '@/store/auth-store';
import { progressionStorage } from '@/services/progression-storage';
import { showDestructiveConfirm } from '@/store/confirmation-store';
import { ProgressionGrid } from './ProgressionGrid';
import { ExploreSection } from './ExploreSection';
import { RecentSection } from './RecentSection';
import { LibraryNav } from './LibraryNav';
import type { SavedProgression } from '@/types/progression';
import styles from './Dashboard.module.css';

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

export const Dashboard: React.FC = () => {
  const { savedProgressions, isLoading, loadProgressions, deleteProgression } = useProgressionsStore();
  const navigateToCanvas = useAppViewStore((s) => s.navigateToCanvas);
  const { user, profile, signOut } = useAuthStore();
  const [recentProgressions, setRecentProgressions] = useState<SavedProgression[]>([]);
  const [activeNav, setActiveNav] = useState<string>('all');
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const displayName = profile?.display_name || user?.email?.split('@')[0] || 'User';

  useEffect(() => {
    loadProgressions();
    progressionStorage.getRecent(5).then(setRecentProgressions);
  }, [loadProgressions]);

  // Compute counts for nav
  const navCounts = useMemo(() => {
    const now = Date.now();
    return {
      all: savedProgressions.length,
      recent: savedProgressions.filter(
        (p) => now - new Date(p.updatedAt).getTime() < SEVEN_DAYS_MS
      ).length,
      starred: savedProgressions.filter((p) => p.isFavorite === true).length,
      imported: savedProgressions.filter((p) => p.analyzedFrom != null).length,
    };
  }, [savedProgressions]);

  // Filter progressions based on active nav + filter pill
  const filteredProgressions = useMemo<SavedProgression[]>(() => {
    const now = Date.now();
    let list = [...savedProgressions];

    // Nav filter
    if (activeNav === 'recent') {
      list = list.filter((p) => now - new Date(p.updatedAt).getTime() < SEVEN_DAYS_MS);
    } else if (activeNav === 'starred') {
      list = list.filter((p) => p.isFavorite === true);
    } else if (activeNav === 'imported') {
      list = list.filter((p) => p.analyzedFrom != null);
    }
    // collections / library items: show all for now (no data to filter against)

    // Toolbar filter pills
    if (activeFilter === 'major') {
      list = list.filter((p) => p.mode === 'major');
    } else if (activeFilter === 'minor') {
      list = list.filter((p) => p.mode === 'minor');
    } else if (activeFilter === 'modal') {
      list = list.filter((p) => p.tags?.some((t) => t.toLowerCase() === 'modal'));
    } else if (activeFilter === 'drafts') {
      list = list.filter((p) => p.tags?.some((t) => t.toLowerCase() === 'draft'));
    }

    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.key.toLowerCase().includes(q) ||
          p.mode.toLowerCase().includes(q) ||
          p.tags?.some((t) => t.toLowerCase().includes(q))
      );
    }

    return list;
  }, [savedProgressions, activeNav, activeFilter, searchQuery]);

  const handleOpenProgression = (progression: SavedProgression) => {
    window.dispatchEvent(new CustomEvent('loadProgression', { detail: progression }));
    navigateToCanvas(progression.id);
  };

  const handleCreateNew = () => {
    navigateToCanvas();
  };

  const handleDeleteProgression = (progression: SavedProgression) => {
    showDestructiveConfirm(
      'Delete Progression',
      `Delete "${progression.title}"? This cannot be undone.`,
      async () => {
        await deleteProgression(progression.id);
        progressionStorage.getRecent(5).then(setRecentProgressions);
      }
    );
  };


  const navHeadingLabel = {
    all: 'My progressions',
    recent: 'Recent',
    starred: 'Starred',
    imported: 'Imported',
    'col-etudes': 'Etudes',
    'col-sketches': 'Sketches',
    'col-hymns': 'Hymns',
    'lib-common': 'Common practice',
    'lib-modal': 'Modal vocabulary',
    'lib-examples': 'Examples & studies',
  }[activeNav] ?? 'My progressions';

  const filterPills = [
    { id: 'all', label: 'All' },
    { id: 'major', label: 'Major' },
    { id: 'minor', label: 'Minor' },
    { id: 'modal', label: 'Modal' },
    { id: 'drafts', label: 'Drafts' },
  ];

  return (
    <div className={styles.dashboard}>
      <div className={styles.layout}>
        <LibraryNav
          activeNav={activeNav}
          onNavChange={setActiveNav}
          counts={navCounts}
        />

        <div className={styles.main}>
          {/* Top header */}
          <header className={styles.topHeader}>
            <div className={styles.topLeft}>
              <h1 className={styles.heading}>
                {activeNav === 'all' ? (
                  <>My <i>progressions</i></>
                ) : (
                  <>{navHeadingLabel}</>
                )}
              </h1>
              <span className={styles.metaLine}>
                {savedProgressions.length} pieces &middot; last opened 2h ago
              </span>
            </div>
            <div className={styles.topActions}>
              <button className={styles.btnGhost}>
                <span className={styles.btnMonoLabel}>Import MusicXML</span>
              </button>
              <button className={styles.btnPrimary} onClick={handleCreateNew}>
                + New progression
              </button>
            </div>
          </header>

          {/* Toolbar */}
          <div className={styles.toolbar}>
            <div className={styles.searchWrap}>
              <input
                className={styles.searchInput}
                placeholder="Find by name, key, mood, or chord…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className={styles.filters}>
              {filterPills.map((pill) => (
                <button
                  key={pill.id}
                  className={`${styles.pill} ${activeFilter === pill.id ? styles.pillActive : ''}`}
                  onClick={() => setActiveFilter(pill.id)}
                >
                  {pill.label}
                </button>
              ))}
            </div>
            <div className={styles.viewToggle}>
              <button
                className={`${styles.viewBtn} ${viewMode === 'grid' ? styles.viewBtnOn : ''}`}
                onClick={() => setViewMode('grid')}
                title="Grid view"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                  <rect x="0" y="0" width="6" height="6" rx="1"/>
                  <rect x="8" y="0" width="6" height="6" rx="1"/>
                  <rect x="0" y="8" width="6" height="6" rx="1"/>
                  <rect x="8" y="8" width="6" height="6" rx="1"/>
                </svg>
              </button>
              <button
                className={`${styles.viewBtn} ${viewMode === 'list' ? styles.viewBtnOn : ''}`}
                onClick={() => setViewMode('list')}
                title="List view"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                  <rect x="0" y="1" width="14" height="2" rx="1"/>
                  <rect x="0" y="6" width="14" height="2" rx="1"/>
                  <rect x="0" y="11" width="14" height="2" rx="1"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Content area */}
          <div className={styles.content}>
            <section className={styles.section}>
              <div className={styles.sectionHead}>
                <h2 className={styles.sectionTitle}>
                  {activeNav === 'all' ? (
                    <>All progressions <em>{filteredProgressions.length} in total</em></>
                  ) : (
                    <>{navHeadingLabel}</>
                  )}
                </h2>
              </div>
              <ProgressionGrid
                progressions={filteredProgressions}
                loading={isLoading}
                onOpen={handleOpenProgression}
                onDelete={handleDeleteProgression}
                onCreateNew={handleCreateNew}
              />
            </section>

            <section className={styles.section}>
              <div className={styles.sectionHead}>
                <h2 className={styles.sectionTitle}>Explore</h2>
              </div>
              <ExploreSection />
            </section>

            {recentProgressions.length > 0 && (
              <section className={styles.section}>
                <div className={styles.sectionHead}>
                  <h2 className={styles.sectionTitle}>
                    Recent <em>past seven days</em>
                  </h2>
                </div>
                <RecentSection
                  progressions={recentProgressions}
                  onOpen={handleOpenProgression}
                />
              </section>
            )}

            <footer className={styles.footer}>
              <div className={styles.userSection}>
                <span className={styles.userName}>{displayName}</span>
                <button className={styles.signOutButton} onClick={signOut}>
                  Sign out
                </button>
              </div>
              <a href="mailto:feedback@neumeapp.com" className={styles.feedbackLink}>
                Send feedback
              </a>
            </footer>
          </div>
        </div>
      </div>
    </div>
  );
};
